import { DownOutlined } from '@ant-design/icons';
import { Dropdown, Image } from 'antd';
import { useState } from 'react';
import { GetUserPortfolioResponse } from 'src/services/user/get-user-portfolio';
import { getChainInfo } from 'src/utils/chainHelpers';
import { useAppTranslation } from 'src/i18n/hooks';
import { Network, Wallet, Check } from 'lucide-react';
import { Card } from 'src/components/ui/card';
import { useAppSelector } from 'src/store';
import { THEME_MODE } from 'src/interfaces/theme';

interface TokenBalance {
  balance: number;
  token_name: string;
  chain_id: number;
}

interface GroupedChainData {
  chain_id: number;
  chain_name: string;
  connected: boolean;
  tokens: TokenBalance[];
}

type ListFundsDropdownProps = {
  chainId?: number;
  listSupportAsset: string[];
  customClassName?: string;
  isLoadingBalanceAllChain: boolean;
  balanceAllChainQuery: GetUserPortfolioResponse | undefined;
  transformedDataBalanceAllChain: TokenBalance[];
  onSelectChain: (chainId: number) => void;
};

const formatNumber = (value?: number) => {
  if (!value) {
    return 0;
  }

  if (Number(value) < 0) {
    return 0;
  }

  return Number(value).toLocaleString('en-US', {
    maximumFractionDigits: 10,
    minimumFractionDigits: 0,
  });
};

const ListFundsDropdown = ({
  chainId,
  customClassName,
  transformedDataBalanceAllChain,
  onSelectChain,
}: ListFundsDropdownProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useAppTranslation(['notifications', 'trade']);
  const userTheme = useAppSelector((state: any) => state.theme);
  const isDarkMode = userTheme === THEME_MODE.DARK;

  const groupedChainData: GroupedChainData[] =
    transformedDataBalanceAllChain?.reduce<GroupedChainData[]>((acc, nav) => {
      const chain = acc?.find((item) => item?.chain_id === nav?.chain_id);
      if (chain) {
        chain.tokens.push(nav);
      } else {
        acc.push({
          chain_id: nav?.chain_id,
          chain_name: getChainInfo(nav?.chain_id).name,
          connected: Number(chainId) === Number(nav?.chain_id),
          tokens: [nav],
        });
      }
      return acc;
    }, []) || [];

  const currentNetwork = groupedChainData.find((chain) => chain.connected);

  const menu = (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 min-w-[320px] network-dropdown-menu">
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-blue-500" />
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {t('trade:withdrawModal.availableNetworks')}
          </span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {t('trade:withdrawModal.selectNetworkToAccess')}
        </p>
      </div>

      <div className="space-y-2 p-2 max-h-80 overflow-y-auto">
        {groupedChainData.map((chain, index) => (
          <Card
            key={index}
            className={`cursor-pointer transition-all duration-200 hover:shadow-md border network-option-card ${
              chain.connected
                ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                : 'border-gray-200 hover:border-blue-300 dark:border-gray-700 dark:hover:border-blue-600'
            }`}
            onClick={(e) => {
              onSelectChain(chain.chain_id);
              setOpen(false);
            }}
          >
            <div className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative flex items-center justify-center">
                    <Image
                      src={getChainInfo(chain.chain_id).logo}
                      alt="network-logo"
                      width={28}
                      height={28}
                      style={{ borderRadius: '50%' }}
                      className="border-2 border-white shadow-sm"
                    />
                    {chain.connected && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {chain.chain_name}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="space-y-1">
                    {chain.tokens.slice(0, 2).map((token, idx) => (
                      <div key={idx} className="flex items-center justify-end gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {formatNumber(token.balance)} {token.token_name}
                        </span>
                      </div>
                    ))}
                    {chain.tokens.length > 2 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
                        +{chain.tokens.length - 2} {t('trade:withdrawModal.moreNetworks')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}

        {groupedChainData.length === 0 && (
          <div className="text-center py-8">
            <Wallet className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('trade:withdrawModal.noNetworksAvailable')}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={customClassName}>
      <Dropdown
        overlay={menu}
        trigger={['click']}
        placement="bottomLeft"
        overlayClassName={`network-dropdown ${isDarkMode ? 'network-dropdown-dark' : ''}`}
        open={open}
        onOpenChange={(val) => setOpen(val)}
      >
        <Card className="cursor-pointer transition-all duration-200 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 border-2 network-selector-card dark:bg-gray-800 dark:border-gray-700">
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex justify-center items-center gap-3">
                <div className="relative flex items-center justify-center">
                  {currentNetwork ? (
                    <>
                      <Image
                        src={getChainInfo(currentNetwork.chain_id).logo}
                        alt="current-network"
                        width={32}
                        height={32}
                        style={{ borderRadius: '50%' }}
                        className="border-2 border-white shadow-sm"
                      />
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    </>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
                      <Network className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {currentNetwork
                      ? currentNetwork.chain_name
                      : t('trade:withdrawModal.selectNetwork')}
                  </span>
                </div>
              </div>
              <DownOutlined
                className={`text-blue-500 transition-transform duration-200 ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </Card>
      </Dropdown>
    </div>
  );
};

export default ListFundsDropdown;
